/**
 * Pure utilities for determining whether a fresh subscription checkout should
 * be blocked.
 *
 * Intentionally uses structural types (no @coeff/api imports) so this module
 * can be unit-tested without ts-jest encountering @coeff/api's compiled JS.
 */
export const shouldBlockEnterpriseCheckout = (
  clientFlags: { enforce_billing_checkout_enterprise_blocker?: boolean } | undefined,
  premiumConnectorsUsed: string[],
  subscriptionDetails: object | null | undefined
): boolean =>
  Boolean(
    clientFlags?.enforce_billing_checkout_enterprise_blocker &&
      premiumConnectorsUsed.length > 0 &&
      !subscriptionDetails
  );

/**
 * Plan types treated as "already paying" for the central-billing checkout gate.
 * Mirrors server-side PAID_SUBSCRIPTION_PLAN_TYPES (coeff/server/spec.py).
 */
export const isPaidPlanType = (planType: string | undefined | null): boolean =>
  ["enterprise", "custom", "pro", "plus", "starter"].includes(planType ?? "");

/**
 * Whether managing the subscription (adding seats / assigning licenses) should be blocked because
 * the premium-connector checkout blocker is active.
 *
 * A licensed paid-plan admin or plan manager must NEVER be blocked here (ENG-8735 — the
 * Affinity/Procore recurrence). It exempts paid users (isPaidPlanType) and anyone who manages
 * billing (canManageBilling), instead of the narrower "plan_type !== enterprise" check it
 * used to have, which missed the `custom` tier (Coefficient's enterprise deals) and every other
 * paid tier, and ignored plan managers/admins entirely.
 */
export const shouldBlockSubscriptionUpdateForPremiumEnforcement = (
  clientFlags:
    | { enforce_billing_checkout_enterprise_blocker?: boolean; plan_type?: string | null }
    | undefined,
  premiumConnectorsUsedCount: number,
  canManageBilling: boolean
): boolean =>
  Boolean(
    clientFlags?.enforce_billing_checkout_enterprise_blocker &&
      premiumConnectorsUsedCount > 0 &&
      !isPaidPlanType(clientFlags?.plan_type) &&
      !canManageBilling
  );
